import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as publicApi from '../../api/publicApi';

export const loadPublicPosts = createAsyncThunk(
  'public/loadPosts',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await publicApi.fetchPublicPosts(params);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erreur de chargement.');
    }
  }
);

export const loadPublicPost = createAsyncThunk(
  'public/loadPost',
  async (id, { rejectWithValue }) => {
    try {
      return await publicApi.fetchPublicPostById(id);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Article non trouvé.');
    }
  }
);

export const loadPublicAuthors = createAsyncThunk(
  'public/loadAuthors',
  async (_, { rejectWithValue }) => {
    try {
      const data = await publicApi.fetchPublicAuthors();
      return data.authorsByRole || {
        administrationModeration: [],
        professors: [],
        spaceStudents: [],
        students: [],
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erreur de chargement.');
    }
  }
);

export const loadPublicAuthor = createAsyncThunk(
  'public/loadAuthor',
  async (id, { rejectWithValue }) => {
    try {
      return await publicApi.fetchPublicAuthor(id);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Auteur non trouvé.');
    }
  }
);

export const loadPublicEvents = createAsyncThunk(
  'public/loadEvents',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await publicApi.fetchPublicEvents(params);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erreur de chargement.');
    }
  }
);

const publicSlice = createSlice({
  name: 'public',
  initialState: {
    posts: [],
    totalPosts: 0,
    postsPage: 1,
    postsLimit: 12,
    currentPost: null,
    author: null,
    authorsByRole: {
      administrationModeration: [],
      professors: [],
      spaceStudents: [],
      students: [],
    },
    events: [],
    loading: false,
    loadingMore: false,
    error: null,
  },
  reducers: {
    clearCurrentPost: (state) => { state.currentPost = null; },
    clearAuthor: (state) => { state.author = null; },
    clearError: (state) => { state.error = null; },
    clearPosts: (state) => { state.posts = []; state.totalPosts = 0; state.postsPage = 1; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPublicPosts.pending, (state, action) => {
        const page = action.meta?.arg?.page ?? 1;
        if (page === 1) state.loading = true;
        else state.loadingMore = true;
        state.error = null;
      })
      .addCase(loadPublicPosts.fulfilled, (state, action) => {
        const { posts = [], total = 0, page = 1, limit = 12 } = action.payload;
        state.postsPage = page;
        state.postsLimit = limit;
        state.totalPosts = total;
        if (page === 1) state.posts = posts;
        else state.posts = [...state.posts, ...posts];
        state.loading = false;
        state.loadingMore = false;
      })
      .addCase(loadPublicPosts.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
      })

      .addCase(loadPublicPost.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loadPublicPost.fulfilled, (state, action) => { state.loading = false; state.currentPost = action.payload; })
      .addCase(loadPublicPost.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(loadPublicAuthors.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loadPublicAuthors.fulfilled, (state, action) => {
        state.loading = false;
        state.authorsByRole = action.payload || state.authorsByRole;
      })
      .addCase(loadPublicAuthors.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(loadPublicAuthor.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loadPublicAuthor.fulfilled, (state, action) => { state.loading = false; state.author = action.payload; })
      .addCase(loadPublicAuthor.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(loadPublicEvents.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loadPublicEvents.fulfilled, (state, action) => { state.loading = false; state.events = action.payload || []; })
      .addCase(loadPublicEvents.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { clearCurrentPost, clearAuthor, clearError, clearPosts } = publicSlice.actions;
export default publicSlice.reducer;
